// PageFAQ.js - Renders FAQ from PageSEO schema_markup (auto-generated by AI)
// Just pass seoData.schema_markup and it renders the FAQs visually

import React, { useState } from 'react';
import styled from 'styled-components';

const extractFAQs = (schemaMarkup) => {
  if (!schemaMarkup) return [];

  // Handle single schema object
  if (schemaMarkup['@type'] === 'FAQPage' && schemaMarkup.mainEntity) {
    return schemaMarkup.mainEntity.map(q => ({
      question: q.name,
      answer: q.acceptedAnswer?.text || '',
    }));
  }

  // Handle array of schemas
  if (Array.isArray(schemaMarkup)) {
    const faqSchema = schemaMarkup.find(s => s['@type'] === 'FAQPage');
    if (faqSchema?.mainEntity) {
      return faqSchema.mainEntity.map(q => ({
        question: q.name,
        answer: q.acceptedAnswer?.text || '',
      }));
    }
  }

  return [];
};

/**
 * PageFAQ — renders a styled accordion FAQ section.
 *
 * Accepts data in two forms (priority order):
 *   faqs        — Array of { q, a } objects (from usePageContent)
 *   schemaMarkup — FAQPage JSON-LD object or array (from useSEO / PageSEO model)
 */
const PageFAQ = ({ schemaMarkup, faqs: faqsProp, title = 'Frequently Asked Questions', fullWidth = false }) => {
  const faqs = faqsProp?.length
    ? faqsProp.map(f => ({ question: f.q, answer: f.a }))
    : extractFAQs(schemaMarkup);
  const [openIndex, setOpenIndex] = useState(0); // First one open by default

  if (faqs.length === 0) return null;

  return (
    <FAQSection $fullWidth={fullWidth}>
      <FAQTitle>{title}</FAQTitle>
      <FAQList>
        {faqs.map((faq, index) => (
          <FAQItem key={index} $isOpen={openIndex === index}>
            <FAQQuestion onClick={() => setOpenIndex(openIndex === index ? -1 : index)}>
              <span>{faq.question}</span>
              <FAQIcon $isOpen={openIndex === index}>
                <span className="material-symbols-outlined">
                  {openIndex === index ? 'remove' : 'add'}
                </span>
              </FAQIcon>
            </FAQQuestion>
            {openIndex === index && (
              <FAQAnswer>{faq.answer}</FAQAnswer>
            )}
          </FAQItem>
        ))}
      </FAQList>
    </FAQSection>
  );
};

const FAQSection = styled.div`
  max-width: 800px;
  width: 100%;
  margin: 48px auto;
  padding: 0 16px;

  @media (min-width: 640px) {
    padding: 0 20px;
  }
`;

const FAQTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 20px;
  text-align: center;

  @media (min-width: 640px) {
    font-size: 24px;
    margin-bottom: 24px;
  }
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FAQItem = styled.div`
  background: ${props => props.$isOpen ? '#f8fafc' : '#fff'};
  border: 1px solid ${props => props.$isOpen ? '#e0e7ff' : '#e5e7eb'};
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
`;

const FAQQuestion = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.5;

  @media (min-width: 640px) {
    padding: 18px 20px;
    gap: 16px;
    font-size: 15px;
  }

  &:hover {
    color: #4f46e5;
  }
`;

const FAQIcon = styled.span`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: ${props => props.$isOpen ? '#4f46e5' : '#f3f4f6'};
  color: ${props => props.$isOpen ? '#fff' : '#6b7280'};
  transition: all 0.2s;

  .material-symbols-outlined {
    font-size: 18px;
  }
`;

const FAQAnswer = styled.div`
  padding: 0 20px 18px 20px;
  font-size: 14px;
  line-height: 1.7;
  color: #4b5563;
`;

export default PageFAQ;
